'use client';

import { Icon, type IconNames, SkeletonBar, TextPassage } from '@petsmart-ui/sparky';
import cn from 'classnames';
import type { InputHTMLAttributes, ReactNode, RefAttributes } from 'react';

import type { FulfillmentMethod, FulfillmentStatus } from '../lib';
import { CarIcon } from './car-icon';
import './fulfillment-tile.scss';

const STATUS_TONE_CLASS_NAMES: Record<NonNullable<FulfillmentStatus['tone']>, string> = {
    success: 'sparky-u-color-utility-success',
    error: 'sparky-u-color-utility-error',
};

type FulfillmentTileProps = Readonly<{
    value: FulfillmentMethod;
    title: ReactNode;
    iconName: IconNames | 'car-outline';
    status?: FulfillmentStatus;
    isDisabled?: boolean;
    isLoading?: boolean;
    isSelected: boolean;
    statusTestId?: string;
    inputProps?: InputHTMLAttributes<HTMLInputElement> & RefAttributes<HTMLInputElement>;
}>;

export const FulfillmentTile = ({
    value,
    title,
    iconName,
    status,
    isSelected,
    isDisabled = false,
    isLoading = false,
    statusTestId,
    inputProps,
    ...props
}: FulfillmentTileProps) => {
    const className = cn(`${value} delivery-tile`, { disabled: isDisabled, selected: isSelected });

    const renderIcon = () => {
        if (iconName === 'car-outline') return <CarIcon isDisabled={isDisabled} />;

        return (
            <Icon
                data-testid="delivery-tile-icon"
                name={iconName}
                className={`delivery-tile__icon ${isDisabled ? 'disabled' : ''}`}
                size="md"
            />
        );
    };

    const renderStatus = () => {
        if (isLoading) return <SkeletonBar data-testid="delivery-tile-skeleton" size="sm" />;

        if (isDisabled) {
            return (
                <TextPassage className="sparky-u-color-utility-error" size="sm">
                    Not available
                </TextPassage>
            );
        }

        if (!status) {
            return null;
        }

        return (
            <TextPassage
                data-testid={statusTestId}
                className={status.tone && STATUS_TONE_CLASS_NAMES[status.tone]}
                size="sm"
            >
                {status.message}
            </TextPassage>
        );
    };

    return (
        <label className={className} data-testid={`${value}-tile`}>
            <input
                type="radio"
                value={value}
                checked={isSelected}
                className="hidden-input"
                disabled={isDisabled}
                {...props}
                {...inputProps}
            />
            {isSelected && (
                <svg
                    data-testid="delivery-tile-checkmark"
                    xmlns="http://www.w3.org/2000/svg"
                    width="11"
                    height="8"
                    viewBox="0 0 10 8"
                    fill="none"
                    className="delivery-tile__checkmark-icon"
                >
                    <path
                        d="M3.56756 7.3997C3.55932 7.3997 3.55116 7.3997 3.54292 7.3989C3.34564 7.39266 3.15892 7.30282 3.03044 7.15434L0.347959 4.09378C0.129559 3.84458 0.154599 3.46562 0.403799 3.2473C0.652599 3.02778 1.03196 3.05354 1.25028 3.30314L3.59292 5.97618L8.7742 0.776181C9.0078 0.541781 9.38828 0.540261 9.62268 0.774581C9.8578 1.00858 9.8578 1.38826 9.62428 1.62298L4.08164 7.18546C3.94612 7.32218 3.7598 7.3997 3.56756 7.3997ZM3.93124 6.36138L3.93556 6.36682C3.93396 6.36522 3.93284 6.3629 3.93124 6.36138ZM3.23196 6.33802C3.23156 6.33882 3.23116 6.33962 3.23036 6.33962L3.23196 6.33802Z"
                        fill="white"
                        opacity={1}
                    />
                </svg>
            )}
            <div className="delivery-tile__content">
                {renderIcon()}
                <TextPassage className="sparky-u-theme-typography-body-lg-bold">{title}</TextPassage>
                <div data-testid="delivery-tile-status-content" className="delivery-tile__content-status">
                    {renderStatus()}
                </div>
            </div>
        </label>
    );
};
